import { Box } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { compact, first, orderBy, uniq } from 'lodash';

import { EditButton } from '@/components/form/baseInputs/Button/EditButton';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { CellContainer } from '@/components/tables/DataTable/components/cells';
import { SHOW_ON_ROW_HOVER_CLASSNAME } from '@/components/tables/DataTable/components/ThemedDataGrid';
import { TwoLineTextRenderer } from '@/components/tables/DataTable/renderers/cell/TwoLineTextRenderer';
import { Column } from '@/components/tables/DataTable/types';
import { STATE_CODE_TO_NAME } from '@/constants/states';
import { LocateSuggestionButton } from '@/modules/aiSuggestions/components/LocateSuggestionButton';
import { getRoleKindDisplayName } from '@/modules/professionalTeam/ClientProfessionalTeam.copy';
import { ClientProfileRelationshipType } from '@/types/schema';
import { formatEnumCase } from '@/utils/formatting/strings';
import { getNodes } from '@/utils/graphqlUtils';
import { getRelationshipCopy } from '@/utils/relationshipCopy';

import {
  ClientPeopleListViewTable_ClientOrganizationFragment,
  ClientPeopleListViewTable_ClientProfileFragment,
  ClientPeopleListViewTableQuery,
} from './graphql/ClientPeopleListViewTable.generated';

export const ROW_TYPE_INDIVIDUAL = 'INDIVIDUAL' as const;
export const ROW_TYPE_ORGANIZATION = 'ORGANIZATION' as const;

/**
 * Minimal onboarding-suggestion shape needed by the people list rows. The row
 * UI only reads the suggestion `id` (to render the locate button and open the
 * suggestion drawer, which fetches full evidence itself), so the list query
 * intentionally selects only `id` rather than the expensive full suggestion
 * fragment.
 */
export type PeopleListSuggestion = NonNullable<
  ClientPeopleListViewTable_ClientProfileFragment['onboardedIndividualSuggestion']
>[number];

/**
 * Explicit order of relationship types for the people list. The array index
 * is used as the sort priority: lower index sorts first. Spouses are listed
 * first because they are most commonly primary clients, but a non-primary
 * spouse should still appear at the top of family relationships.
 */
export const PEOPLE_LIST_RELATIONSHIP_ORDER: ClientProfileRelationshipType[] = [
  ClientProfileRelationshipType.Spouse,
  ClientProfileRelationshipType.Child,
  ClientProfileRelationshipType.Grandchild,
  ClientProfileRelationshipType.DomesticPartner,
  ClientProfileRelationshipType.Parent,
  ClientProfileRelationshipType.Sibling,
  ClientProfileRelationshipType.StepChild,
  ClientProfileRelationshipType.Grandparent,
  ClientProfileRelationshipType.NephewNiece,
  ClientProfileRelationshipType.UncleAunt,
  ClientProfileRelationshipType.Cousin,
  ClientProfileRelationshipType.OtherRelative,
  ClientProfileRelationshipType.ExSpouse,
  ClientProfileRelationshipType.Friend,
  ClientProfileRelationshipType.OtherNonRelative,
];

export const NO_RELATIONSHIP_SORT_PRIORITY =
  PEOPLE_LIST_RELATIONSHIP_ORDER.length;
export const ORGANIZATION_SORT_PRIORITY =
  PEOPLE_LIST_RELATIONSHIP_ORDER.length + 1;

export interface RowType {
  lineOne: string;
  lineTwo: string;
  id: string;
  kind: typeof ROW_TYPE_INDIVIDUAL | typeof ROW_TYPE_ORGANIZATION;
  actions?: null;
  relationshipSortPriority: number;
  onboardingSuggestion?: PeopleListSuggestion | null;
}

function getRelationshipSortPriority(
  relationshipsWithPrimaryClients: {
    type: ClientProfileRelationshipType;
  }[]
): number {
  if (relationshipsWithPrimaryClients.length === 0) {
    return NO_RELATIONSHIP_SORT_PRIORITY;
  }

  const priority = Math.min(
    ...relationshipsWithPrimaryClients.map((rel) => {
      const index = PEOPLE_LIST_RELATIONSHIP_ORDER.indexOf(rel.type);
      return index === -1 ? NO_RELATIONSHIP_SORT_PRIORITY : index;
    })
  );

  return priority;
}

export function mapIndividualToRowType(
  individual: ClientPeopleListViewTable_ClientProfileFragment,
  primaryClients: ClientPeopleListViewTable_ClientProfileFragment[]
): RowType {
  let lineTwo = '';
  if (individual.isPrimary) {
    const fullStateName = individual.address?.stateCode
      ? STATE_CODE_TO_NAME[individual.address.stateCode.toUpperCase()]
      : undefined;
    lineTwo = compact(['Client', fullStateName]).join(' | ');
  }

  const relationships = individual.relationships || [];
  const relationshipsWithPrimaryClients = relationships.filter((rel) => {
    const relatedClient = primaryClients.find(
      (client) => client.id === rel.toClientProfile.id && client.isPrimary
    );
    return !!relatedClient;
  });
  const relationshipSortPriority = getRelationshipSortPriority(
    relationshipsWithPrimaryClients
  );
  const relationshipsText = getRelationshipCopy(relationships, primaryClients);

  if (relationshipsText) {
    lineTwo = compact([lineTwo, relationshipsText]).join(', ');
  }

  const professionalTeamRoles = getNodes(individual.professionalTeamRoles);
  if (professionalTeamRoles.length) {
    const professionalTeamRolesCopy = professionalTeamRoles.map((role) =>
      getRoleKindDisplayName({
        kind: role.kind,
        otherRoleName: role.otherRoleName || '',
        powerOfAttorneyKind: role.powerOfAttorneyKind,
      })
    );
    // only show each professional team role once, enforce with `_.uniq`
    lineTwo = uniq(compact([lineTwo, ...professionalTeamRolesCopy])).join(', ');
  }

  return {
    lineOne: individual.displayName,
    lineTwo,
    id: individual.id,
    kind: ROW_TYPE_INDIVIDUAL,
    relationshipSortPriority,
    onboardingSuggestion: individual.onboardedIndividualSuggestion?.[0] ?? null,
  };
}

function mapOrganizationToRowType(
  organization: ClientPeopleListViewTable_ClientOrganizationFragment
): RowType {
  return {
    lineOne: organization.name,
    lineTwo: formatEnumCase(organization.kind) || '',
    id: organization.id,
    kind: ROW_TYPE_ORGANIZATION,
    relationshipSortPriority: ORGANIZATION_SORT_PRIORITY,
    onboardingSuggestion:
      organization.onboardedOrganizationSuggestion?.[0] ?? null,
  };
}

export function mapDataToTableRows(
  data: ClientPeopleListViewTableQuery | undefined
): RowType[] {
  const advisorClient = first(getNodes(data?.households));
  if (!advisorClient) {
    return [];
  }

  const primaryClients = getNodes(data?.primaryClients);

  const individuals = compact(advisorClient.clientProfiles || [])
    .filter((individual) => !individual.isPrimary) // Filter out primary clients as they're shown above
    .map((individual) => mapIndividualToRowType(individual, primaryClients));
  const organizations = compact(advisorClient.clientOrganizations || []).map(
    mapOrganizationToRowType
  );

  return orderBy(
    [...individuals, ...organizations],
    ['relationshipSortPriority', 'lineOne'],
    ['asc', 'asc']
  );
}

export const getClientPeopleListViewTableColumns = (
  canEdit: boolean,
  onLocateSuggestionClick?: (suggestion: PeopleListSuggestion) => void,
  onEditClick?: (row: RowType) => void
): Column<RowType>[] =>
  compact([
    {
      headerName: 'Name',
      renderCell: TwoLineTextRenderer({
        lineOne: ({ row }) => row.lineOne,
        lineTwo: ({ row }) => row.lineTwo,
        textAccompaniment: ({ row }) => (
          <ClientPeopleOrganizationRowSuggestionLocatorButton
            row={row}
            onLocateSuggestionClick={onLocateSuggestionClick}
          />
        ),
      }),
      flex: 1,
      field: 'lineOne',
    },
    canEdit &&
      onEditClick && {
        headerName: '',
        field: 'actions',
        align: 'center',
        width: 64,
        minWidth: 64,
        maxWidth: 64,
        sortable: false,
        renderCell: (params: GridRenderCellParams<RowType>) => {
          const tooltipTitle =
            params.row.kind === ROW_TYPE_INDIVIDUAL
              ? 'Edit individual'
              : 'Edit organization';
          return (
            <CellContainer align={params.colDef.align}>
              <Tooltip title={tooltipTitle} placement="top-start">
                <Box>
                  <EditButton
                    size="xs"
                    variant="transparent"
                    onClick={() => onEditClick(params.row)}
                    iconProps={{ size: 16 }}
                  />
                </Box>
              </Tooltip>
            </CellContainer>
          );
        },
      },
  ]);

interface ClientPeopleOrganizationRowSuggestionLocatorButtonProps {
  row: RowType;
  onLocateSuggestionClick?: (suggestion: PeopleListSuggestion) => void;
}

function ClientPeopleOrganizationRowSuggestionLocatorButton({
  row,
  onLocateSuggestionClick,
}: ClientPeopleOrganizationRowSuggestionLocatorButtonProps) {
  if (!row.onboardingSuggestion || !onLocateSuggestionClick) {
    return null;
  }

  return (
    <Box
      className={SHOW_ON_ROW_HOVER_CLASSNAME}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <LocateSuggestionButton
        suggestionID={row.onboardingSuggestion.id}
        onLocateSuggestionClick={() =>
          onLocateSuggestionClick(row.onboardingSuggestion!)
        }
        dataTestId={`people-locate-suggestion-${row.id}`}
      />
    </Box>
  );
}
